/**
 * WebSocket service for real-time bidirectional communication
 *
 * Provides a WebSocket connection manager with automatic reconnection,
 * message queueing, and connection state management.
 */

export type WebSocketConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error'

export interface WebSocketMessage<T = unknown> {
  type: string
  data: T
  timestamp?: number
}

export interface WebSocketOptions {
  /** Reconnection delay in milliseconds (default: 3000) */
  reconnectDelay?: number
  /** Maximum reconnection attempts (default: 5, -1 for infinite) */
  maxReconnectAttempts?: number
  /** Whether to automatically reconnect on error (default: true) */
  autoReconnect?: boolean
  /** Whether to queue messages sent while disconnected (default: true) */
  queueMessages?: boolean
  /** Maximum queue size (default: 100) */
  maxQueueSize?: number
  /** Ping interval in milliseconds (default: 30000, 0 to disable) */
  pingInterval?: number
}

type MessageHandler<T = unknown> = (message: WebSocketMessage<T>) => void
type StateHandler = (state: WebSocketConnectionState, error?: Error) => void

const DEFAULT_OPTIONS: Required<WebSocketOptions> = {
  reconnectDelay: 3000,
  maxReconnectAttempts: 5,
  autoReconnect: true,
  queueMessages: true,
  maxQueueSize: 100,
  pingInterval: 30000
}

export class WebSocketService {
  private ws: WebSocket | null = null
  private url: string
  private options: Required<WebSocketOptions>
  private messageHandlers: Map<string, Set<MessageHandler>> = new Map()
  private stateHandlers: Set<StateHandler> = new Set()
  private reconnectAttempts = 0
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null
  private pingTimeout: ReturnType<typeof setTimeout> | null = null
  private messageQueue: WebSocketMessage[] = []
  private _state: WebSocketConnectionState = 'disconnected'

  constructor(url: string, options: WebSocketOptions = {}) {
    this.url = url
    this.options = { ...DEFAULT_OPTIONS, ...options }
  }

  get state(): WebSocketConnectionState {
    return this._state
  }

  private setState(state: WebSocketConnectionState, error?: Error): void {
    this._state = state
    this.stateHandlers.forEach(handler => handler(state, error))
  }

  /**
   * Connect to the WebSocket endpoint
   */
  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return
    }

    this.cleanup()
    this.setState('connecting')

    try {
      this.ws = new WebSocket(this.url)

      this.ws.onopen = () => {
        this.reconnectAttempts = 0
        this.setState('connected')
        this.flushMessageQueue()
        this.startPing()
      }

      this.ws.onclose = (event) => {
        this.stopPing()
        if (!event.wasClean) {
          this.handleReconnect()
        } else {
          this.setState('disconnected')
        }
      }

      this.ws.onerror = () => {
        const error = new Error('WebSocket connection error')
        this.setState('error', error)
      }

      this.ws.onmessage = (event) => {
        this.handleMessage(event)
      }
    } catch (error) {
      this.setState('error', error instanceof Error ? error : new Error('Failed to connect'))
      this.handleReconnect()
    }
  }

  /**
   * Handle incoming messages
   */
  private handleMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data)

      // Handle pong responses
      if (data.type === 'pong') {
        return
      }

      const message: WebSocketMessage = {
        type: data.type || 'message',
        data: data.data || data,
        timestamp: Date.now()
      }
      this.dispatchMessage(message)
    } catch {
      // If not JSON, treat as raw message
      const message: WebSocketMessage<string> = {
        type: 'message',
        data: event.data,
        timestamp: Date.now()
      }
      this.dispatchMessage(message)
    }
  }

  /**
   * Dispatch message to registered handlers
   */
  private dispatchMessage(message: WebSocketMessage): void {
    // Dispatch to specific type handlers
    const typeHandlers = this.messageHandlers.get(message.type)
    typeHandlers?.forEach(handler => handler(message))

    // Dispatch to wildcard handlers
    const wildcardHandlers = this.messageHandlers.get('*')
    wildcardHandlers?.forEach(handler => handler(message))
  }

  /**
   * Handle reconnection logic
   */
  private handleReconnect(): void {
    this.cleanup()

    if (!this.options.autoReconnect) {
      this.setState('disconnected')
      return
    }

    if (this.options.maxReconnectAttempts !== -1 &&
        this.reconnectAttempts >= this.options.maxReconnectAttempts) {
      this.setState('disconnected')
      return
    }

    this.reconnectAttempts++
    const delay = this.options.reconnectDelay * Math.min(this.reconnectAttempts, 5)

    this.reconnectTimeout = setTimeout(() => {
      this.connect()
    }, delay)
  }

  /**
   * Start ping interval for keep-alive
   */
  private startPing(): void {
    if (this.options.pingInterval <= 0) return

    this.pingTimeout = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.send({ type: 'ping', data: null })
      }
    }, this.options.pingInterval)
  }

  /**
   * Stop ping interval
   */
  private stopPing(): void {
    if (this.pingTimeout) {
      clearInterval(this.pingTimeout)
      this.pingTimeout = null
    }
  }

  /**
   * Flush queued messages when connected
   */
  private flushMessageQueue(): void {
    while (this.messageQueue.length > 0 && this.ws?.readyState === WebSocket.OPEN) {
      const message = this.messageQueue.shift()
      if (message) {
        this.ws.send(JSON.stringify(message))
      }
    }
  }

  /**
   * Send a message through the WebSocket
   */
  send<T>(message: WebSocketMessage<T>): boolean {
    const messageWithTimestamp: WebSocketMessage<T> = {
      ...message,
      timestamp: Date.now()
    }

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(messageWithTimestamp))
      return true
    }

    if (this.options.queueMessages) {
      if (this.messageQueue.length >= this.options.maxQueueSize) {
        this.messageQueue.shift() // Remove oldest message
      }
      this.messageQueue.push(messageWithTimestamp)
    }

    return false
  }

  /**
   * Register a handler for a specific message type
   * Use '*' to receive all messages
   */
  on<T = unknown>(type: string, handler: MessageHandler<T>): () => void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, new Set())
    }
    this.messageHandlers.get(type)!.add(handler as MessageHandler)

    // Return unsubscribe function
    return () => {
      this.messageHandlers.get(type)?.delete(handler as MessageHandler)
    }
  }

  /**
   * Register a handler for connection state changes
   */
  onStateChange(handler: StateHandler): () => void {
    this.stateHandlers.add(handler)
    // Immediately call with current state
    handler(this._state)

    return () => {
      this.stateHandlers.delete(handler)
    }
  }

  /**
   * Clean up WebSocket and timeouts
   */
  private cleanup(): void {
    this.stopPing()

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }

    if (this.ws) {
      this.ws.onclose = null
      this.ws.onerror = null
      this.ws.onmessage = null
      this.ws.onopen = null
      this.ws.close()
      this.ws = null
    }
  }

  /**
   * Disconnect from the WebSocket endpoint
   */
  disconnect(): void {
    this.cleanup()
    this.reconnectAttempts = 0
    this.messageQueue = []
    this.setState('disconnected')
  }

  /**
   * Reset reconnection attempts and try to connect
   */
  reconnect(): void {
    this.reconnectAttempts = 0
    this.connect()
  }
}

/**
 * Create a new WebSocket service instance
 */
export function createWebSocketService(url: string, options?: WebSocketOptions): WebSocketService {
  return new WebSocketService(url, options)
}
